import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { PayrollPeriodService } from 'src/app/services/payroll-period.service';
import { PayrollService } from 'src/app/services/payroll.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

interface StatusMeta {
    label: string;
    bg: string;
    color: string;
}

type TabKey = 'all' | 'pending' | 'stamped' | 'error';

const STATUS_META: { [key: string]: StatusMeta } = {
    stamped: { label: 'Timbrado', bg: '#e3fbe8', color: '#3c7e43' },
    pending: { label: 'Pendiente', bg: '#fef3d4', color: '#cf8515' },
    error: { label: 'Error', bg: '#ffccce', color: '#c5010b' }
};

/**
 * "Consulta de periodo": vista de solo lectura de los recibos electrónicos
 * de un periodo de nómina específico, alcanzada desde una fila de
 * "Administrador de periodos" (node Figma "administrador de
 * periodos/consulta de periodos anteriores").
 */
@Component({
    selector: 'app-payroll-period-receipts',
    templateUrl: './payroll-period-receipts.component.html',
    styleUrls: ['./payroll-period-receipts.component.css']
})
export class PayrollPeriodReceiptsComponent implements OnInit {

    periodId!: number;
    period: any = null;

    filterSearch = '';
    activeTab: TabKey = 'all';
    private searchDebounce: any;

    allRows: any[] = [];
    stats = { total: 0, stamped: 0, pending: 0, error: 0, net_period_total: 0 };

    loading = false;
    loadError = false;

    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 25, 50];
    public currentPage = 1;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private payrollPeriodService: PayrollPeriodService,
        private payrollService: PayrollService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Recibos electrónicos', action);
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe((params) => {
            const idParam = params.get('id');
            this.periodId = idParam ? parseInt(idParam, 10) : 0;
            if (!this.periodId) return;
            this.loadPeriod();
            this.fetchReceipts();
        });
    }

    loadPeriod(): void {
        this.payrollPeriodService.show(this.periodId).subscribe({
            next: (resp: any) => { this.period = resp; },
            error: () => { this.period = null; }
        });
    }

    fetchReceipts(): void {
        this.loading = true;
        this.loadError = false;
        this.spinner.show();
        const params: any = { period_id: this.periodId };
        if (this.filterSearch) params.search = this.filterSearch;
        if (this.activeTab !== 'all') params.cfdi_status = this.activeTab;

        this.payrollService.listReceipts(params).subscribe({
            next: (resp: any) => {
                this.allRows = Array.isArray(resp?.data) ? resp.data : [];
                this.stats = resp?.stats || this.stats;
                this.currentPage = 1;
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.allRows = [];
                this.loading = false;
                this.loadError = true;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el listado de recibos del periodo', icon: 'error' });
            }
        });
    }

    onSearchChanged(): void {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => this.fetchReceipts(), 350);
    }

    setTab(tab: TabKey): void {
        this.activeTab = tab;
        this.fetchReceipts();
    }

    // ---- Paginación (client-side: el periodo ya acota el conjunto) ----
    get totalRows(): number {
        return this.allRows.length;
    }

    get lastPage(): number {
        return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
    }

    get visibleRows(): any[] {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.allRows.slice(start, start + this.pageSize);
    }

    onPageSizeChanged(newSize: number | string): void {
        const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;
        if (!isNaN(size)) {
            this.pageSize = size;
            this.currentPage = 1;
        }
    }

    nextPage(): void {
        if (this.currentPage < this.lastPage) this.currentPage += 1;
    }

    prevPage(): void {
        if (this.currentPage > 1) this.currentPage -= 1;
    }

    // ---- Navegación ----
    volverAlAdminDePeriodos(): void {
        this.router.navigateByUrl('/payroll-periods');
    }

    viewReceipt(data: any): void {
        this.router.navigateByUrl(`/employee/${data.employee_id}/payroll/${data.id}`);
    }

    downloadPdf(data: any): void {
        this.spinner.show();
        this.payrollService.downloadPdf(data.id).subscribe({
            next: (blob: Blob) => {
                this.spinner.hide();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `recibo-nomina-${data.payroll_number}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo descargar el recibo', icon: 'error' });
            }
        });
    }

    // ---- Timbrado ----
    // Aspel NOI: una vez cerrado el periodo, aquí es donde se revisan y
    // timbran los recibos electrónicos de ese periodo (ver stampBulk).
    stampSingle(data: any): void {
        if (!this.canDo('Timbrar')) return;
        this.spinner.show();
        this.payrollService.stampBulk({ id: data.id }).subscribe({
            next: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'El recibo se timbró correctamente', icon: 'success' });
                this.fetchReceipts();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo timbrar el recibo', icon: 'error' });
            }
        });
    }

    stampMassive(): void {
        if (!this.canDo('Timbrar')) return;
        if (!this.stats.pending) {
            this.utils.alertSwal({ title: 'Sin pendientes', text: 'No hay recibos pendientes por timbrar en este periodo', icon: 'info' });
            return;
        }
        this.spinner.show();
        const params: any = { period_id: this.periodId };
        if (this.filterSearch) params.search = this.filterSearch;
        this.payrollService.stampBulk(params).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: `${resp?.updated || 0} recibo(s) timbrado(s) correctamente`, icon: 'success' });
                this.fetchReceipts();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo realizar el timbrado masivo', icon: 'error' });
            }
        });
    }

    // ---- KPIs ----
    get stampedPercent(): string { return this.pct(this.stats.stamped); }
    get pendingPercent(): string { return this.pct(this.stats.pending); }
    get errorPercent(): string { return this.pct(this.stats.error); }

    private pct(count: number): string {
        if (!this.stats.total) return '0.0';
        return ((count / this.stats.total) * 100).toFixed(1);
    }

    // ---- Helpers ----
    getEmployeeName(data: any): string {
        const employee = data?.employee;
        if (!employee) return '--';
        const parts = [employee.first_name, employee.last_name].filter(Boolean);
        return parts.join(' ') || '--';
    }

    formatPeriodRange(data: any): string {
        if (!data) return '--';
        return `${this.formatDate(data.period_start)} al ${this.formatDate(data.period_end)}`;
    }

    formatCurrency(value: any): string {
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    get periodDateLabel(): string {
        return this.period?.period_date ? this.formatDate(this.period.period_date) : '';
    }

    statusMeta(value: string): StatusMeta | undefined {
        return STATUS_META[value];
    }
}
